import React from "react";
import { Dispatch } from "redux";
import { RouteComponentProps } from "react-router";
import Timer from "../../components/Timer/Timer";
import LiveGraph from "../../components/Exercise/LiveGraph/LiveGraphRaw";
import { hideSideBar, showSideBar } from "../../actions/navBar.actions";
import * as sensorDataAnalysis from "../../utils/sensorDataAnalysis";
import {
  uploadActivity,
  handleActivityStatus,
  handleCurrentRepUpdate,
  handleTimerStatus,
  handleCalibrationStatus,
} from "../../actions/activities.actions";
import { Dimmer, Header } from "semantic-ui-react";
import Navigation from "../../components/Exercise/Navigation/Navigation";
import { connect } from "react-redux";
import { State } from "../../reducers";
import {
  handleBluetoothState,
  bluetoothConnection,
} from "../../actions/bluetooth.actions";
import "./Exercise.css";
import GlobalBluetoothWrapper from "../../utils/GlobalBluetoothWrapper";

interface ExerciseProps extends RouteComponentProps<{}, {}, { selectedSetup?: string }> {
  uploadedId: any;
  activityStatus: string;
  bluetoothConnected: boolean;
  calibrationStatus: string;
  weightUnit: string;
  bluetoothStatus: string;
  dispatch: Dispatch<any>;
  sensorData?: number | null;
  shouldStartWorkout?: boolean;
}
interface ExerciseState {
  graphData: any[];
  paused: boolean;
  strength: number;
  pulledStrength: any;
  maxTime: number;
  maxValue: number;
  done: boolean;
  showCalibrationModal: boolean;
  startingTime: number;
}
const mapStateToProps = (state: State) => ({
  uploadedId: state.activities.uploadedId,
  activityStatus: state.activities.activityStatus,
  bluetoothConnected: state.bluetooth.bluetoothConnected,
  calibrationStatus: state.activities.calibrationStatus,
  weightUnit: state.authentication.user.displaySettings.weightUnit,
  bluetoothStatus: state.bluetooth.bluetoothStatus,
});
let weightUnitFactor;
class Exercise extends React.Component<ExerciseProps, ExerciseState> {
  private pulledWeight1: any;
  private lastPulledWeight: number;
  private globalBluetoothWrapper: GlobalBluetoothWrapper;

  constructor(props: ExerciseProps) {
    super(props);
    this.state = {
      done: false,
      graphData: [],
      paused: false,
      strength: 0,
      pulledStrength: 0,
      maxTime: 0,
      maxValue: 0,
      showCalibrationModal: false,
      startingTime: Date.now(),
    };
    this.pulledWeight1 = 0;
    this.lastPulledWeight = 0;
    this.globalBluetoothWrapper = GlobalBluetoothWrapper.getInstance();
    this.props.dispatch(
      handleCurrentRepUpdate({
        time: 216000,
        intensity: 0,
        guideline: '',
        fingers: [[], []],
        expectedStrength: 0,
        setIndex: 0,
        repIndex: 0,
        setCount: 0,
        repCount: 0,
        holds: [],
        img: '',
        audio: '',
        grip: 0,
      })
    );
    this.startActivity = this.startActivity.bind(this);
    this.pauseActivity = this.pauseActivity.bind(this);
    this.restartActivity = this.restartActivity.bind(this);
    this.finishActivity = this.finishActivity.bind(this);
    this.updateGraph = this.updateGraph.bind(this);
    this.updateTime = this.updateTime.bind(this);
    this.updateStrength = this.updateStrength.bind(this);
  }

  componentDidMount() {
    this.props.dispatch(hideSideBar());
    this.props.dispatch(handleBluetoothState("off"));
    this.props.dispatch(bluetoothConnection(false));
    this.props.dispatch(handleActivityStatus("stop"));

    sensorDataAnalysis.freeDataArray();
    weightUnitFactor = this.props.weightUnit === "kg" ? 1 : 2.2;
  }
  componentWillUnmount() {
    this.props.dispatch(handleActivityStatus("stop"));
    this.props.dispatch(handleBluetoothState("off"));
    sensorDataAnalysis.freeDataArray();
  }
  componentDidUpdate(prevProps: ExerciseProps) {
    if (this.props.activityStatus !== prevProps.activityStatus) {
      if (this.props.activityStatus === "reset") {
        const nextState =
          this.props.bluetoothStatus === "off" ? "stop" : "calibrate";
        this.props.dispatch(handleActivityStatus(nextState));
        if (!this.state.done) this.setState({ done: true });
      }
    }
    
    if (this.props.sensorData !== prevProps.sensorData && this.props.sensorData !== null) {
      this.updateStrength(this.props.sensorData);
    }
    
    if (this.props.shouldStartWorkout && !prevProps.shouldStartWorkout) {
      this.startActivity();
    }
    
    // Note: Data stream and workout start are now managed by ExerciseWithGlobalBluetooth
  }
  startActivity() {
    const actStatus = "hang";
    
    if (this.props.activityStatus !== "pause") {
      sensorDataAnalysis.onStart();
      // Reset starting time when starting a new activity
      this.setState({ startingTime: Date.now() });
    }
    
    this.props.dispatch(handleActivityStatus(actStatus));
    this.props.dispatch(handleTimerStatus(actStatus));
    this.setState({
      paused: false,
    });
  }

  pauseActivity() {
    if (this.props.activityStatus === "continue " || this.props.activityStatus === "start ") {
      this.props.dispatch(handleActivityStatus("pause"));
      this.props.dispatch(handleTimerStatus("pause"));
      this.setState({
        paused: true,
      });
    }
  }
  restartActivity() {
    this.setState({
      graphData: [],
      paused: false,
    });
    sensorDataAnalysis.freeDataArray();
    this.props.dispatch(handleTimerStatus("stop"));
    this.props.dispatch(handleCalibrationStatus("stop"));
    this.props.dispatch(handleBluetoothState("off"));
    this.props.dispatch(bluetoothConnection(false)); // Reset Bluetooth connection state
    this.globalBluetoothWrapper.stopDataStream();
    this.props.dispatch(handleActivityStatus("stop"));
  }

  async finishActivity() {
    this.props.dispatch(handleTimerStatus("stop"));
    
    if (sensorDataAnalysis._weightDataArray.length > 0) {
      // Get calibrated values from global Bluetooth context
      const globalBluetoothWrapper = GlobalBluetoothWrapper.getInstance();
      const calibrationState = globalBluetoothWrapper.getCalibrationState();
      const userWeight = calibrationState.userWeight || 0;
      const addedWeights = calibrationState.addedWeights || 0;
      
      // Ensure we have the required state for sensorDataAnalysis.onEnd()
      const workoutState = (this.props.location as any).state || {
        selectedWorkout: 'raw',
        type: 'none',
        title: 'Free hanging',
        notes: '',
        selectedSetup: 'raw'
      };
      
      const archive = await sensorDataAnalysis.onEnd(
        userWeight, // Use calibrated user weight
        workoutState,
        addedWeights  // Use calibrated added weights
      );
      this.restartActivity();
      // @ts-ignore
      this.props.dispatch(uploadActivity(archive)).then((uploadedId: number) => {
        this.props.history.push(`/activities/${uploadedId}`);
        this.props.dispatch(showSideBar());
      }).catch((error: any) => {
        console.error('Exercise: Failed to upload activity:', error);
        // Redirect to home page if upload fails
        this.props.history.push('/');
        this.props.dispatch(showSideBar());
      });
    } else {
      // Still redirect to home page even if no data
      this.props.history.push('/');
      this.props.dispatch(showSideBar());
    }
  }
  handleOut() {
    if (this.pulledWeight1 === "Out") {
      return "MAX";
    }
    if ((this.props.location as any).state?.selectedSetup === "hangboard") {
      // Return user weight for hangboard workouts
      const globalBluetoothWrapper = GlobalBluetoothWrapper.getInstance();
      const calibrationState = globalBluetoothWrapper.getCalibrationState();
      return calibrationState.userWeight || 0;
    }
    return 0;
  }
  updateGraph(time: number) {
    let strengthData;
    
    if (this.pulledWeight1 === "Out") {
      strengthData = this.handleOut();
    } else {
      // Use the already processed pulled force data from ExerciseWithGlobalBluetooth
      strengthData = this.pulledWeight1;
    }
    
    // Cap the strength data at minimum 0 to prevent negative values
    strengthData = Math.max(0, strengthData);
    
    const dataToAdd = Math.floor(strengthData * 100) / 100;
    sensorDataAnalysis.addDataToString(dataToAdd);

    this.setState((state) => {
      const newDataPoint = {
        x: time,
        y: strengthData * weightUnitFactor,
      };
      
      // Add new data point
      let graphData: any[] = state.graphData.concat([newDataPoint]);
      
      // Keep only the last 10 seconds of data (rolling window)
      const tenSecondsAgo = time - 10;
      graphData = graphData.filter(dataPoint => dataPoint.x >= tenSecondsAgo);

      // Calculate max value from current data
      let value = Math.max.apply(
        Math,
        graphData.map((o: any) => o.y)
      );
      value = value <= 5 ? 5 : value;
      
      return {
        graphData,
        maxValue: parseFloat(value),
        maxTime: time,
      };
    });
  }

  updateTime(time: number) {
    // Only process data during the hang phase (actual workout)
    if (this.props.activityStatus === "hang") this.updateGraph(time);
  }

  updateStrength(sensorData: any) {
    this.pulledWeight1 = sensorData;
    
    // Only process data during the hang phase (actual workout)
    if (this.props.activityStatus === 'hang') {
      const currentTime = (Date.now() - this.state.startingTime) / 1000;
      this.updateGraph(currentTime);
    }
  }

  // Method to handle Bluetooth data from ExerciseWithGlobalBluetooth wrapper
  handleBluetoothData(sensorData: any) {
    this.updateStrength(sensorData);
  }

  showPulledStrength() {
    // Use the already processed data from ExerciseWithGlobalBluetooth
    let displayForce;
    
    if (this.pulledWeight1 === "Out") {
      displayForce = this.handleOut();
    } else {
      // The data is already processed in ExerciseWithGlobalBluetooth, just use it directly
      displayForce = this.pulledWeight1;
    }
    
    // Cap the display force at minimum 0 to prevent negative values
    displayForce = Math.max(0, displayForce);
    
    let html = () => (
      <>
        <span
          style={{ fontFamily: "open sans", fontWeight: 400, fontSize: "3.5rem", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {`${Math.floor(1 * displayForce * weightUnitFactor)}.${Math.floor(
            10 *
              (displayForce * weightUnitFactor -
                Math.floor(displayForce * weightUnitFactor))
          )}`}
        </span>
      </>
    );
    if (this.pulledWeight1 === "Out")
      html = () => (
        <span
          style={{ fontFamily: "open sans", fontWeight: 700, fontSize: "5rem" }}
        >
          {this.handleOut()}
        </span>
      );
    return <span className="rawStrength">{html()}</span>;
  }
  render() {
    return (
      <>
        <div
          className="ui one column grid exerciseManager-container-raw"
          onClick={this.pauseActivity}
        >
            <i
              className="close icon finishExercise"
              style={{ fontSize: "3.5em", color: "white" }}
              onClick={this.finishActivity}
            />
            <div className="timerRawPosition">
              <Timer updateTime={this.updateTime} raw />
            </div>
            {this.showPulledStrength()}
            <div className="row livegraph" style={{ padding: "0px" }}>
              <LiveGraph
                chartData={this.state.graphData}
                maxTime={this.state.maxTime}
                maxValue={this.state.maxValue}
              />
            </div>
          </div>

        <Dimmer active={this.state.paused} verticalAlign="center">
          <Header as="h2" inverted>
            Paused
          </Header>

          <Navigation
            leftIcon={"stop"}
            leftIconAction={this.finishActivity}
            middleIcon={"play"}
            middleIconAction={this.startActivity}
            rightIcon={"undo"}
            rightIconAction={this.restartActivity}
            style={{ bottom: "auto" }}
          ></Navigation>
        </Dimmer>
      </>
    );
  }
}

const ExerciseWithRef = React.forwardRef<any, ExerciseProps>((props, ref) => {
  return <Exercise ref={ref} {...props} />;
});

export default connect(mapStateToProps)(ExerciseWithRef);
